import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import React from 'react';
import { FormattedList, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';

import type { SetCollectionTaskDescription_taskInterface } from './__generated__/index.graphql.ts';

const Team = styled(Horizontal).attrs({ center: true })`
  display: inline-flex;
`;

type Props = {
  task: SetCollectionTaskDescription_taskInterface;
};

export const SetCollectionTaskDescription = ({ task }: Props) => {
  const { title, description } = task;
  const defaultDescription = (
    <Vertical gap={0}>
      <LabelL bold>{title}</LabelL>
      <LabelL>{description}</LabelL>
    </Vertical>
  );
  if (!isType(task, 'CardsCountTask')) {
    return defaultDescription;
  }
  const { scoreToReach, specifiedTeams } = task;
  if (!specifiedTeams?.length) {
    return defaultDescription;
  }
  return (
    <LabelL>
      <FormattedMessage
        defaultMessage="<b>Collect {count} players</b>from {teams}"
        values={{
          count: scoreToReach,
          b: chunks => <LabelL bold>{chunks}</LabelL>,
          teams: (
            <FormattedList
              type="conjunction"
              value={specifiedTeams.filter(isTypeFn('Club')).map(team => (
                <Team key={team.slug} center as="span">
                  {team.code}
                  {!!team.pictureUrl && (
                    <img src={team.pictureUrl} alt="" height={16} />
                  )}
                </Team>
              ))}
            />
          ),
        }}
      />
    </LabelL>
  );
};

SetCollectionTaskDescription.fragments = {
  taskInterface: gql`
    fragment SetCollectionTaskDescription_taskInterface on TaskInterface {
      id
      title
      description
      ... on CardsCountTask {
        id
        scoreToReach
        specifiedTeams {
          ... on Club {
            slug
            pictureUrl
            code
          }
        }
      }
    }
  ` as TypedDocumentNode<SetCollectionTaskDescription_taskInterface>,
};
